import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NzIconService } from 'ng-zorro-antd/icon';
import { APP_ICONS } from '../constants/icons.constants';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CustomIconService {
  /**
   * Base path where custom SVG icons are stored.
   */
  protected readonly iconPath = 'assets/icons/';

  /**
   * Array of custom icon names defined in constants.
   */
  protected readonly CUSTOM_ICONS = APP_ICONS;

  private readonly iconService = inject(NzIconService);
  private readonly http = inject(HttpClient);

  /**
   * Adds all custom icons defined in the application by loading their SVG content
   * and registering them with the NzIconService.
   *
   * @returns A promise that resolves once all icons are successfully registered.
   */
  public async addCustomIcons(): Promise<void> {
    const customIcons = this.CUSTOM_ICONS;

    // Create a list of promises to load each custom icon
    const iconPromises = customIcons.map((name) => {
      const fileName = `${name}.svg`;
      return this.loadIcon(name, fileName);
    });

    try {
      // Wait for all icons to load and register
      await Promise.all(iconPromises);
    } catch (error) {
      console.error('Some icons failed to load:', error);
    }
  }

  /**
   * Loads an individual custom SVG icon from the file system and registers it with NzIconService.
   *
   * @param name - The name to register the icon under.
   * @param fileName - The filename of the SVG file to load.
   * @returns A promise that resolves once the icon is successfully loaded and registered.
   *
   * @throws Will throw an error if the SVG file cannot be loaded or registered.
   */
  private async loadIcon(name: string, fileName: string): Promise<void> {
    const filePath = `${this.iconPath}${fileName}`;

    try {
      // Fetch the SVG content using HttpClient
      let svgContent = await firstValueFrom(
        this.http.get(filePath, { responseType: 'text' }),
      );

      // Remove 'width' and 'height' attributes from the SVG content
      svgContent = this.cleanSVG(svgContent);

      // Register the SVG content as a custom icon with a namespace
      this.iconService.addIconLiteral(`icons:${name}`, svgContent!);
    } catch (error) {
      console.error(`Failed to load icon ${name} from ${filePath}:`, error);
      throw error; // Rethrow to propagate error to the caller
    }
  }

  /**
   * Cleans the SVG content by removing 'width' and 'height' attributes and ensuring
   * other attributes are preserved correctly.
   *
   * @param svgContent - The raw SVG content as a string.
   * @returns The cleaned SVG content.
   */
  private cleanSVG(svgContent: string): string {
    const parser = new DOMParser();
    const serializer = new XMLSerializer();

    // Parse the SVG content into a DOM element
    const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');
    const svgElement = svgDoc.documentElement;

    // Remove 'width' and 'height' attributes
    svgElement.removeAttribute('width');
    svgElement.removeAttribute('height');

    // Serialize the cleaned SVG content back to a string
    return serializer.serializeToString(svgElement);
  }
}
